import React, {useCallback, useEffect, useRef, useState} from "react";
import {ProductService} from "../product.service";
import {useDispatch} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {ProductImage} from "./product-image";
import {changePageTitle, setAlert} from "../../typescript/redux/actions";
import CustomButtonGroup from "../../typescript/custom-button-group/custom-button-group";
import CustomButton from "../../typescript/custom-button/custom-button";
import {ColorButtonEnum} from "../../typescript/util/color-button-enum";
import {Box, Button, Card, CardMedia, IconButton, Tooltip} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const ProductImageList = React.memo(() => {

    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);

    let navigate = useNavigate();
    const {id} = useParams();
    if (!id) {
        throw new Error('ID required');
    }
    const [images, setImages] = useState<ProductImage[]>([]);

    const findAll = useCallback(async () => {
        const result = await ProductService.findAllImages(parseInt(id));
        setImages(result);
    }, [id]);

    useEffect(() => {
        dispatch(changePageTitle("Imagens do produto " + id));
        findAll().then();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const goBack = async () => {
        navigate(-1);
    }

    const handleCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (id && files?.length) {
            const productId = parseInt(id);
            let successCount = 0;
            let errorCount = 0;
            for (let i = 0; i < files.length; i++) {
                try {
                    await ProductService.addImage(productId, files[i]);
                    successCount++;
                } catch {
                    errorCount++;
                }
            }
            if (successCount > 0) {
                dispatch(setAlert({
                    show: true,
                    message: `${successCount} imagem(ns) enviada(s) com sucesso${errorCount > 0 ? `, ${errorCount} falha(s)` : ''}`,
                    severity: errorCount > 0 ? 'warning' : 'success'
                }));
            } else if (errorCount > 0) {
                dispatch(setAlert({
                    show: true,
                    message: 'Erro ao enviar imagens',
                    severity: 'error'
                }));
            }
            findAll().then();
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDeleteImage = async (img: ProductImage) => {
        if (id) {
            const productId = parseInt(id);
            try {
                await ProductService.deleteImage(productId, img.id);
                dispatch(setAlert({show: true, message: 'Imagem excluída com sucesso', severity: 'success'}));
                findAll().then();
            } catch (error: any) {
                let message = 'Erro ao excluir imagem';
                if (error.response && error.response.data && error.response.data.message) {
                    message = (message + ' - ' + error.response.data.message);
                }
                dispatch(setAlert({show: true, message: message, severity: 'error'}));
            }
        }
    };

    const renderAddImageButton = () => {
        if (images.length >= 5) {
            return (
                <Tooltip title="O limite de imagens é 5" arrow>
                    <span>
                        <Button color="primary" component="label" disabled>
                            Adicionar imagem
                            <input type="file" hidden multiple accept="image/png" onChange={handleCapture} ref={fileInputRef}/>
                        </Button>
                    </span>
                </Tooltip>
            );
        }
        return (
            <Button color="primary" component="label">Adicionar imagem
                <input type="file" hidden multiple accept="image/png" onChange={handleCapture} ref={fileInputRef}/>
            </Button>
        );
    }

    return (
        <React.Fragment>
            <CustomButtonGroup>
                <CustomButton variant="outlined" type="button" color={ColorButtonEnum.INHERIT} onClick={() => goBack()}>
                    Voltar
                </CustomButton>
                {renderAddImageButton()}
            </CustomButtonGroup>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    "& > *": {
                        flex: "1 0 calc(20% - 16px)",
                        "@media (max-width:900px)": {flex: "1 0 50%"},
                        "@media (max-width:600px)": {flex: "1 0 100%"},
                        maxWidth: "100%",
                        position: "relative"
                    },
                }}
            >
                {images.map((img) => (
                    <Card key={img.id} sx={{position: "relative"}}>
                        <CardMedia
                            component="img"
                            height="200"
                            image={img.url}
                            alt={`Imagem ${img.id}`}
                            sx={{objectFit: "contain", background: "#f5f5f5"}}
                        />
                        <Tooltip title="Excluir" arrow>
                            <IconButton
                                size="small"
                                sx={{
                                    position: "absolute",
                                    right: 8,
                                    bottom: 8,
                                    background: "rgba(255,255,255,0.8)",
                                    "&:hover": {background: "rgba(255,0,0,0.2)"}
                                }}
                                onClick={() => handleDeleteImage(img)}
                            >
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </Card>
                ))}
            </Box>
        </React.Fragment>
    );

});

export default ProductImageList;